import React from 'react';
import { isMobile, isTablet } from "react-device-detect";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Button from '@material-ui/core/Button';

import { useAuth } from '../../../../../../core/contexts/AuthContext';

// Roles listing table. Default roles (IsDefault === "True") are read-only; other roles expose
// Edit Name / Edit Permssion (Setup > Role Details > Edit) and delete (Manage > Geo Bounds > Edit).
const UserRoleListItem = props => {
  const { pageSizeChanged, pageNoChanged, editRoleName, editPermission, deleteRole } = props;
  const items = props.items.Data;
  const paging = props.items.Paging;

  const { chkRolePermissions } = useAuth();

  // The Edit Name / Edit Permssion / delete cells are gated on permissions granted by AuthContext's
  // async /identity/rolepermission fetch. When that grant lands AFTER the table has first rendered,
  // primereact's row memoization keeps the (already-rendered) cells stale, so the actions never
  // appear. Key the table on the grants so it remounts once when the permissions resolve.
  const canEditRole = chkRolePermissions("Setup", "Role Details", "Edit");
  const canDeleteRole = chkRolePermissions("Manage", "Geo Bounds", "Edit");

  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const onPage = (event) => {
    const { page, rows } = event;
    if (Number(paging.PageSize) !== rows) {
      pageSizeChanged(rows);
    } else if (Number(paging.PageSize) !== page + 1) {
      pageNoChanged(page + 1);
    }
  };

  const validationStatus = rowData => {
    if (rowData.IsDefault === "True") {
      return (<strong className="opt-l-number-green">True</strong>);
    }
    return (<strong className="opt-l-number-red">False</strong>);
  };

  const setEditContents = (row) => {
    if (row.IsDefault === "True") {
      return "";
    }
    if (!chkRolePermissions("Setup", "Role Details", "Edit")) {
      return "";
    }
    return (
      <span
        onClick={() => editRoleName(row)}
        onKeyDown={(e) => handleKeyDown(e, () => editRoleName(row))}
        role="button"
        tabIndex="0"
      >
        <Button variant={isMobile ? "outlined" : "text"} color="primary" style={{ textTransform: 'none' }}>Edit Name</Button>
      </span>
    );
  };

  const setPermissionContents = (row) => {
    if (row.IsDefault === "True") {
      return "";
    }
    if (!chkRolePermissions("Setup", "Role Details", "Edit")) {
      return "";
    }
    return (
      <span
        onClick={() => editPermission(row)}
        onKeyDown={(e) => handleKeyDown(e, () => editPermission(row))}
        role="button"
        tabIndex="0"
      >
        <Button variant={isMobile ? "outlined" : "text"} color="primary" style={{ textTransform: 'none' }}>Edit Permssion</Button>
      </span>
    );
  };

  const setDeleteContents = (row) => {
    if (row.IsDefault === "True") {
      return "";
    }
    if (!chkRolePermissions("Manage", "Geo Bounds", "Edit")) {
      return "";
    }
    return (
      <span
        onClick={() => deleteRole(row)}
        onKeyDown={(e) => handleKeyDown(e, () => deleteRole(row))}
        role="button"
        tabIndex="0"
      >
        <Button variant={isMobile ? "outlined" : "text"} color="secondary" style={{ textTransform: 'none' }}>X</Button>
      </span>
    );
  };

  const setResponsiveContents = (title, contents, isTitle = true) => {
    return (
      <>
        {isTitle && <span className="p-column-title">{title}</span>}
        <span className="p-column-content">{contents}</span>
      </>
    );
  };

  const setNormalDataTable = () => {
    return (
      <DataTable
        key={`roles-${canEditRole}-${canDeleteRole}`}
        rowHover
        style={{ position: "relative" }}
        value={items}
        className="p-datatable-gridlines  p-datatable-gridlines-custom p-datatable-sm p-datatable-striped p-datatable-striped-custom"
        resizableColumns columnResizeMode="fit"
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        rows={Number(paging.PageSize)}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        totalRecords={Number(paging.TotalNumberOfRecords)}
        lazy onPage={onPage} first={Number(paging.PageSize) * (Number(paging.PageNumber) - 1)}
        scrollable scrollHeight="calc(100vh - 390px)"
      >
        <Column field="RoleName" header="Role Name" style={{ width: '70%' }} />
        <Column field="IsDefault" header="Default" body={r => validationStatus(r)} style={{ width: '10%' }} />
        <Column field="" header="Edit" body={r => setEditContents(r)} style={{ width: '10%' }} />
        <Column field="" header="Permission" body={r => setPermissionContents(r)} style={{ width: '10%' }} />
        <Column field="" header="" body={r => setDeleteContents(r)} style={{ width: '5%' }} />
      </DataTable>
    );
  };

  const setResponsiveDataTable = () => {
    return (
      <DataTable
        key={`roles-responsive-${canEditRole}-${canDeleteRole}`}
        header='Roles'
        style={{ position: "relative" }}
        value={items}
        className="p-datatable-responsive p-datatable-sm "
        paginator
        paginatorTemplate="CurrentPageReport  PageLinks  RowsPerPageDropdown"
        currentPageReportTemplate=" {first} to {last} of {totalRecords}"
        rows={Number(paging.PageSize)}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        totalRecords={Number(paging.TotalNumberOfRecords)}
        lazy onPage={onPage} first={Number(paging.PageSize) * (Number(paging.PageNumber) - 1)}
      >
        <Column header="Role Name" body={r => setResponsiveContents('Role Name', r.RoleName)} />
        <Column header="Default" body={r => setResponsiveContents('Default', validationStatus(r))} />
        <Column header="Edit" body={r => setResponsiveContents('Edit', setEditContents(r))} />
        <Column header="Permission" body={r => setResponsiveContents('Permission', setPermissionContents(r))} />
        <Column header="Delete" body={r => setResponsiveContents('Delete', setDeleteContents(r))} />
      </DataTable>
    );
  };

  const setDataTable = () => {
    if (isMobile && !isTablet) {
      return setResponsiveDataTable();
    }
    return setNormalDataTable();
  };

  return (
    <div
      className="perfect-scrollbar"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      {setDataTable()}
    </div>
  );
};

export default UserRoleListItem;
